import { LOCALE_COOKIE, DEFAULT_LANGUAGE } from '@src/config/localisation/locale-config.constants'
import { shouldEnableI18nClient } from '@src/server-side/utils/i18n-preview.utils'

import isBrowser from '../../src/quince-site-common/src/utils/isBrowser'
import getCookie from '../quince-site-common/src/utils/getCookie'

/**
 * Storage options for managing localized and global session data
 * @property isGlobal - Determines how the key is stored and accessed:
 *   - true: Uses original key (backward compatible, shared across all languages)
 *   - false: Uses {locale}:key format (language-specific data) - DEFAULT
 */
interface SessionStorageOptions {
  isGlobal?: boolean
}

/**
 * Simple enhanced session storage class that adds locale support when needed
 *
 * Key Format:
 * - Global keys (isGlobal=true): originalKey (unchanged for backward compatibility)
 * - Localized keys (isGlobal=false): {locale}:originalKey (e.g., en-us:userPrefs) - DEFAULT
 */
class EnhancedSessionStorage {
  private isI18nEnabled: boolean

  constructor(isI18nEnabled: boolean) {
    this.isI18nEnabled = isI18nEnabled
  }

  private getLocalizedKey(key: string, options: SessionStorageOptions = {}): string {
    const { isGlobal = true } = options

    // If global or i18n disabled, use original key (backward compatible)
    if (isGlobal || !this.isI18nEnabled) {
      return key
    }

    // For localized data, prefix with current locale
    const locale = getCookie(LOCALE_COOKIE) ?? DEFAULT_LANGUAGE
    return `${locale}:${key}`
  }

  /**
   * Retrieves an item from session storage with optional locale support
   * @param key - The key to retrieve
   * @param options - Storage options (optional)
   * @param options.isGlobal - true: use original key, false: use locale:key format (default: false)
   * @returns The stored value or null if not found
   *
   * @example
   * // Localized data (default behavior)
   * const preferences = sessionStorage.getItem('userPrefs')
   * // Looks for: "en-us:userPrefs"
   *
   * // Global data (explicit)
   * const authToken = sessionStorage.getItem('authToken', { isGlobal: true })
   * // Looks for: "authToken"
   */
  getItem<T>(key: string, options: SessionStorageOptions = {}): T | null {
    if (!isBrowser()) return null

    try {
      const sessionKey = this.getLocalizedKey(key, options)
      const item = sessionStorage.getItem(sessionKey)
      return item as T | null
    } catch (error) {
      console.error('Error getting item from session storage:', error)
      return null
    }
  }

  /**
   * Stores an item with optional locale support
   * @param key - The key to store
   * @param value - The value to store
   * @param options - Storage options (optional)
   * @param options.isGlobal - true: use original key, false: use locale:key format (default: false)
   *
   * @example
   * // Localized data (default behavior)
   * sessionStorage.setItem('userPrefs', preferences)
   * // Stores as: "en-us:userPrefs" and removes old "userPrefs" if it exists
   *
   * // Global data (explicit)
   * sessionStorage.setItem('authToken', token, { isGlobal: true })
   * // Stores as: "authToken"
   */
  setItem(key: string, value: string, options: SessionStorageOptions = {}): void {
    if (!isBrowser()) return

    try {
      const { isGlobal = false } = options
      const sessionKey = this.getLocalizedKey(key, options)
      sessionStorage.setItem(sessionKey, value)

      // Clean up old non-localized version when setting localized data
      if (!isGlobal && this.isI18nEnabled && sessionKey !== key) {
        sessionStorage.removeItem(key)
      }
    } catch (error) {
      console.error('Error setting item in session storage:', error)
    }
  }

  /**
   * Convenience method for storing JSON objects
   * @param key - The key to store
   * @param value - The object to store (will be JSON.stringify'd)
   * @param options - Storage options (optional)
   */
  setItemJSON<T>(key: string, value: T, options: SessionStorageOptions = {}): void {
    try {
      const jsonValue = JSON.stringify(value)
      this.setItem(key, jsonValue, options)
    } catch (error) {
      console.error('Error serializing value to JSON:', error)
    }
  }

  /**
   * Convenience method for getting JSON objects with automatic parsing
   * @param key - The key to retrieve
   * @param options - Storage options (optional)
   * @returns Parsed object or null if not found/invalid JSON
   */
  getItemJSON<T>(key: string, options: SessionStorageOptions = {}): T | null {
    const item = this.getItem<string>(key, options)
    if (!item) return null

    try {
      return JSON.parse(item) as T
    } catch (error) {
      console.error('Error parsing JSON from session storage:', error)
      return null
    }
  }

  /**
   * Removes an item from session storage
   * @param key - The key to remove
   * @param options - Storage options (optional)
   */
  removeItem(key: string, options: SessionStorageOptions = {}): void {
    if (!isBrowser()) return

    try {
      const sessionKey = this.getLocalizedKey(key, options)
      sessionStorage.removeItem(sessionKey)
    } catch (error) {
      console.error('Error removing item from session storage:', error)
    }
  }

  /**
   * Checks if an item exists in session storage
   * @param key - The key to check
   * @param options - Storage options (optional)
   * @returns true if the item exists, false otherwise
   */
  hasItem(key: string, options: SessionStorageOptions = {}): boolean {
    if (!isBrowser()) return false

    try {
      const sessionKey = this.getLocalizedKey(key, options)
      return !!sessionStorage.getItem(sessionKey)
    } catch (error) {
      console.error('Error checking item in session storage:', error)
      return false
    }
  }
}

// Create a singleton instance
let sessionStorageInstance: EnhancedSessionStorage | null = null

export const getSessionStorage = (): EnhancedSessionStorage => {
  sessionStorageInstance ??= new EnhancedSessionStorage(shouldEnableI18nClient())
  return sessionStorageInstance
}
